import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { List, X } from "@phosphor-icons/react";

export default function Header() {
    const { user, logout } = useAuth();
    const authed = user && typeof user === "object";
    const [menuOpen, setMenuOpen] = useState(false);
    const closeMenu = () => setMenuOpen(false);

    return (
        <header className="border-b border-[#2a2a2a] bg-[#0a0a0a]/95 backdrop-blur sticky top-0 z-40">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 h-16 flex items-center justify-between gap-2">
                {/* BRAND — condensed on mobile so it doesn't overlap CTAs */}
                <Link
                    to="/"
                    className="flex items-center gap-2 sm:gap-3 min-w-0"
                    data-testid="brand-link"
                    onClick={closeMenu}
                >
                    <img
                        src="/smartnote2self-logo.png"
                        alt="SmartNote2Self"
                        className="block w-8 h-8 sm:w-9 sm:h-9 rounded-sm flex-shrink-0"
                        data-testid="brand-logo"
                    />
                    <div className="leading-tight min-w-0">
                        <div className="hidden sm:block text-[13px] tracking-[0.28em] uppercase text-[#c2c2c2] truncate">
                            SmartNote2Self
                        </div>
                        <div className="font-display text-sm sm:text-base md:text-lg tracking-tight truncate">
                            License Risk Scan
                        </div>
                    </div>
                </Link>

                {/* DESKTOP NAV */}
                <nav className="hidden md:flex items-center gap-2">
                    <NavLink
                        to="/"
                        className={({ isActive }) =>
                            `btn-ghost ${isActive ? "text-white" : ""}`
                        }
                        data-testid="nav-home"
                    >
                        Overview
                    </NavLink>
                    <NavLink
                        to="/plans"
                        className={({ isActive }) =>
                            `btn-ghost ${isActive ? "text-white" : ""}`
                        }
                        data-testid="nav-plans"
                    >
                        Plans
                    </NavLink>
                    {authed && (
                        <>
                            <NavLink
                                to="/dashboard"
                                className={({ isActive }) =>
                                    `btn-ghost ${isActive ? "text-white" : ""}`
                                }
                                data-testid="nav-dashboard"
                            >
                                Dashboard
                            </NavLink>
                            <NavLink
                                to="/assessment"
                                className={({ isActive }) =>
                                    `btn-ghost ${isActive ? "text-white" : ""}`
                                }
                                data-testid="nav-assessment"
                            >
                                Begin Scan for Client
                            </NavLink>
                            {user.role === "admin" && (
                                <NavLink
                                    to="/admin"
                                    className={({ isActive }) =>
                                        `btn-ghost ${isActive ? "text-white" : ""}`
                                    }
                                    data-testid="nav-admin"
                                >
                                    Admin
                                </NavLink>
                            )}
                        </>
                    )}
                </nav>

                {/* DESKTOP RIGHT-SIDE CTAs (auth) */}
                <div className="hidden md:flex items-center gap-2">
                    {!authed ? (
                        <>
                            <Link
                                to="/login"
                                className="btn-ghost"
                                data-testid="header-login-link"
                            >
                                Sign in
                            </Link>
                            <Link
                                to="/register"
                                className="btn-primary"
                                data-testid="header-register-link"
                            >
                                Begin Scan
                            </Link>
                        </>
                    ) : (
                        <>
                            <span className="hidden lg:inline text-xs text-[#c2c2c2] mr-2 truncate max-w-[180px]">
                                {user.email}
                            </span>
                            <Link
                                to="/settings"
                                className="btn-ghost"
                                data-testid="header-settings-link"
                            >
                                Settings
                            </Link>
                            <button
                                className="btn-secondary"
                                onClick={logout}
                                data-testid="header-logout-button"
                            >
                                Sign out
                            </button>
                        </>
                    )}
                </div>

                {/* MOBILE — hamburger only. Compact enough to fit on the smallest handsets. */}
                <button
                    type="button"
                    onClick={() => setMenuOpen((v) => !v)}
                    aria-label={menuOpen ? "Close menu" : "Open menu"}
                    aria-expanded={menuOpen}
                    className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-[#2a2a2a] text-white hover:bg-[#141414] transition-colors flex-shrink-0"
                    data-testid="mobile-menu-toggle"
                >
                    {menuOpen ? <X size={20} /> : <List size={20} />}
                </button>
            </div>

            {/* MOBILE MENU PANEL */}
            {menuOpen && (
                <div
                    className="md:hidden border-t border-[#2a2a2a] bg-[#0a0a0a]"
                    data-testid="mobile-menu-panel"
                >
                    <nav className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex flex-col divide-y divide-[#2a2a2a]">
                        <MobileNavItem to="/" onClick={closeMenu} testId="mobile-nav-home">
                            Overview
                        </MobileNavItem>
                        <MobileNavItem to="/plans" onClick={closeMenu} testId="mobile-nav-plans">
                            Plans
                        </MobileNavItem>
                        {authed && (
                            <>
                                <MobileNavItem to="/dashboard" onClick={closeMenu} testId="mobile-nav-dashboard">
                                    Dashboard
                                </MobileNavItem>
                                <MobileNavItem to="/assessment" onClick={closeMenu} testId="mobile-nav-assessment">
                                    Begin Scan for Client
                                </MobileNavItem>
                                {user.role === "admin" && (
                                    <MobileNavItem to="/admin" onClick={closeMenu} testId="mobile-nav-admin">
                                        Admin
                                    </MobileNavItem>
                                )}
                                <MobileNavItem to="/settings" onClick={closeMenu} testId="mobile-nav-settings">
                                    Settings
                                </MobileNavItem>
                                <div className="py-3 flex items-center justify-between gap-3">
                                    <div className="text-xs text-[#c2c2c2] truncate">
                                        Signed in as <span className="text-white">{user.email}</span>
                                    </div>
                                    <button
                                        onClick={() => {
                                            closeMenu();
                                            logout();
                                        }}
                                        className="btn-secondary text-xs"
                                        data-testid="mobile-header-logout-button"
                                    >
                                        Sign out
                                    </button>
                                </div>
                            </>
                        )}
                        {!authed && (
                            <div className="py-3 flex flex-col gap-2">
                                <Link
                                    to="/login"
                                    onClick={closeMenu}
                                    className="btn-secondary text-center"
                                    data-testid="mobile-header-login-link"
                                >
                                    Sign in
                                </Link>
                                <Link
                                    to="/register"
                                    onClick={closeMenu}
                                    className="btn-primary text-center"
                                    data-testid="mobile-header-register-link"
                                >
                                    Begin Scan
                                </Link>
                            </div>
                        )}
                    </nav>
                </div>
            )}
        </header>
    );
}

function MobileNavItem({ to, onClick, testId, children }) {
    return (
        <NavLink
            to={to}
            onClick={onClick}
            end={to === "/"}
            className={({ isActive }) =>
                `py-3 text-base tracking-tight transition-colors ${
                    isActive ? "text-white" : "text-[#c2c2c2] hover:text-white"
                }`
            }
            data-testid={testId}
        >
            {children}
        </NavLink>
    );
}
